"use client";

import { FormCheckbox } from "@client/components/forms/form-checkbox";
import { FormCurrencyInput } from "@client/components/forms/form-currency-input";
import { FormInput } from "@client/components/forms/form-input";
import {
  createRoundWizardRound,
  type CreateRoundWizardRound,
} from "@engine/round/actions";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
} from "@sfc/ui";
import { ChevronRightIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

type RoundWizardRoundProps = {
  isSfcAdmin: boolean;
  data: CreateRoundWizardRound;
  updateData: (data: CreateRoundWizardRound) => void;
  onNext: (data: CreateRoundWizardRound) => void;
  onIsSfcRoundChange?: (isSfcRound: boolean) => void;
};

export function RoundWizardRound({
  isSfcAdmin,
  data,
  updateData,
  onNext,
  onIsSfcRoundChange,
}: RoundWizardRoundProps) {
  const form = useForm<CreateRoundWizardRound>({
    resolver: zodResolver(createRoundWizardRound),
    defaultValues: data,
  });
  const isSfcRound = form.watch("isSfcRound");

  useEffect(() => {
    onIsSfcRoundChange?.(isSfcRound);
  }, [isSfcRound, onIsSfcRoundChange]);

  const onSubmit = (values: CreateRoundWizardRound) => {
    const normalizedRoundData: CreateRoundWizardRound = {
      ...values,
      initials: values.initials.toUpperCase(),
    };
    updateData(normalizedRoundData);
    onNext(normalizedRoundData);
  };

  return (
    <Form {...form}>
      <Card>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          <CardHeader>
            <CardTitle>Round settings</CardTitle>
            <CardDescription>
              A round is a collection of proposals that will be evaluated
              together.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <FormInput
              className="flex-1"
              control={form.control}
              name="name"
              label="Round Name"
              placeholder="e.g., Q2 2025 Funding Round"
            />

            <div className="flex gap-4">
              <FormCurrencyInput
                className="flex-1"
                control={form.control}
                name="budget"
                label="Round Budget"
              />

              <FormInput
                className="flex-1"
                inputClassName="uppercase"
                control={form.control}
                name="initials"
                label="Your Initials"
                placeholder="e.g., JP"
              />
            </div>

            {isSfcAdmin && (
              <FormCheckbox
                control={form.control}
                name="usesTracks"
                label="Uses tracks"
                description="When off, the round has no tracks: all recommenders sit in the Main track and track-related views are hidden."
              />
            )}
          </CardContent>

          <CardFooter className="flex justify-end">
            {isSfcAdmin && (
              <FormCheckbox
                className="flex-1"
                control={form.control}
                name="isSfcRound"
                label="SFC Round"
              />
            )}
            <Button type="submit">
              {isSfcAdmin && isSfcRound ? "Create" : "Continue"}
              <ChevronRightIcon className="size-4" />
            </Button>
          </CardFooter>
        </form>
      </Card>
    </Form>
  );
}
